import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import type { ProtocolEditorView } from '@/lib/protocol-search';
import { cn } from '@/lib/utils';
import { Icon } from '@pms-digital/design-system';
import { useId } from 'react';

/**
 * La bascule de l'éditeur de protocole : « Conduite de l'entretien » /
 * « Analyste » (analyste.md §3.5, N47 ; planche `AnalysteA`) et « Rangement »
 * (scenario-succes.md §4 ; maquette `EditeurRangement`).
 *
 * « Analyste » is the segment that was « Lecture pour l'analyste » (#154, noyau.md
 * §4.9): the section replaced the view and the position kept its address. The icon
 * is the same open book, and so are the radio's mark and its test id - a selector
 * written for the segment reaches the segment.
 *
 * ## A group of REAL radios
 *
 * The maquette draws `button role="radio"`, which is what a designer draws for
 * lack of a form control; the repository's rule is the native radio
 * (`page-editor/segmented.tsx`, `model-panel.tsx`): the grouping, the arrow
 * keys and the announced state come from the browser, and a Playwright
 * `.check()` reaches the control an operator clicks. The input FILLS its
 * segment, transparent, for the reason `Segmented` gives - a 1 px input under a
 * label is an element no pointer can reach.
 *
 * It is NOT `Segmented` itself: that one is the page editor's joined control in
 * grey, and this one is the maquette's - bleu nuit, two icons, and a mark.
 *
 * ## The yellow mark, and what it announces
 *
 * A protocol whose section is incomplete says so ON the segment, so it is seen
 * without switching (§3.5): an 8 px point in `jaune-deep`, ringed in white so it
 * stays a point on the bleu nuit of a selected segment (R5 - « la marque suit
 * l'état "incomplète" et reste donc sur le segment sélectionné, cerclée de
 * blanc »). A point is invisible to a screen reader, so the segment's NAME carries
 * « , incomplète » - read with the label, the way the maquette hides it.
 *
 * ## It is navigation, not an edit
 *
 * The position lives in the URL (N47): this component says which one was
 * asked for and the route writes `?vue=`. So it stays usable in read-only - a
 * version opened by « Voir » has a section to read as much as a draft does.
 *
 * ## Thirds of the width on a phone
 *
 * Side by side on one line, the first two labels alone measure about 349 px,
 * and a phone of 390 px leaves the editor 342: the body scrolled sideways,
 * which `admin-responsive.spec.ts` refuses on `/admin/protocoles/nouveau`
 * (review of !474). With the third segment of #177 (« Rangement ») the line is
 * longer still, so under `sm` the group is a grid of three equal columns
 * across the whole width, each cell its icon over its label, free to wrap on
 * two or three centred lines (the longest word, « l'entretien », fits a third
 * of 342 px); from `sm` on it is the joined control of the maquette, as wide
 * as its labels. « Analyste » is shorter than the label it replaced, which
 * only makes the middle cell lighter.
 */

export interface ProtocolViewSwitchProps {
  /** The position the URL names. */
  readonly view: ProtocolEditorView;
  /** The section Analyste is incomplete - a level without objective, or an empty reading: the mark. */
  readonly incomplete: boolean;
  readonly onSelect: (view: ProtocolEditorView) => void;
}

interface Segment {
  readonly view: ProtocolEditorView;
  readonly label: string;
  /** Material Symbols name. */
  readonly icon: string;
  readonly testId: string;
}

const SEGMENTS: readonly Segment[] = [
  {
    view: 'conduite',
    label: 'Conduite de l’entretien',
    icon: 'route',
    testId: PROTOCOL_TEST_IDS.viewConduct,
  },
  {
    view: 'lecture',
    label: 'Analyste',
    icon: 'menu_book',
    testId: PROTOCOL_TEST_IDS.viewReading,
  },
  {
    view: 'rangement',
    label: 'Rangement',
    icon: 'checklist',
    testId: PROTOCOL_TEST_IDS.viewRangement,
  },
];

export function ProtocolViewSwitch({ view, incomplete, onSelect }: ProtocolViewSwitchProps) {
  // One native group per editor, never a literal name: two editors on one page
  // would otherwise be one group, and the second would clear the first.
  const name = useId();

  return (
    <div
      role="radiogroup"
      aria-label="Partie de l’éditeur"
      data-testid={PROTOCOL_TEST_IDS.viewSwitch}
      className="grid w-full grid-cols-3 overflow-hidden rounded-md border-2 border-bleu-nuit has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-bleu-nuit has-[:focus-visible]:outline-offset-2 sm:inline-flex sm:w-auto sm:self-start"
    >
      {SEGMENTS.map((segment, index) => {
        const chosen = segment.view === view;
        const marked = segment.view === 'lecture' && incomplete;
        return (
          <label
            key={segment.view}
            className={cn(
              'relative inline-flex min-w-0 cursor-pointer select-none flex-col items-center justify-center gap-1 px-2 py-1.5 text-center font-semibold text-small transition-colors sm:flex-row sm:justify-start sm:gap-1.5 sm:whitespace-nowrap sm:px-3 sm:text-left',
              index === 0 ? '' : 'border-bleu-nuit border-l-2',
              chosen
                ? 'bg-bleu-nuit text-white'
                : 'bg-white text-bleu-nuit hover:bg-bleu-nuit-tint',
            )}
          >
            <input
              type="radio"
              name={name}
              value={segment.view}
              checked={chosen}
              data-testid={segment.testId}
              onChange={() => onSelect(segment.view)}
              className="absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none opacity-0 focus:outline-none"
            />
            <span className="inline-flex shrink-0">
              <Icon name={segment.icon} size={16} />
            </span>
            <span>{segment.label}</span>
            {marked ? (
              <>
                <span className="sr-only">, incomplète</span>
                <span
                  aria-hidden="true"
                  data-testid={PROTOCOL_TEST_IDS.viewReadingMark}
                  className="inline-block size-2 shrink-0 rounded-full bg-jaune-deep ring-2 ring-white"
                />
              </>
            ) : null}
          </label>
        );
      })}
    </div>
  );
}
